import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { finalize } from 'rxjs';
import _ from 'lodash';
import { ExpenseClaimJournalLine } from '../../../models/expense-claim/expense-claim';
import { ReceiptOwnerCode } from '../../../models/expense-receipt/expense-receipt';
import {
  ExpenseSettleable,
  ExpenseSettlement,
  ExpenseSettlementAdvance,
  ExpenseSettlementClaim,
  ExpenseSettlementDirection,
  ExpenseSettlementJournalPreview,
  ExpenseSettlementOwner,
  ExpenseSettlementStatus,
  ExpenseSettlementUpdate,
} from '../../../models/expense-settlement/expense-settlement';
import {
  ExpenseSettlementFormModel,
  ExpenseSettlementLineFormModel,
  expenseSettlementFieldLabels,
} from '../../../models/expense-settlement/expense-settlement-form';
import { FundingBankAccount } from '../../../models/fund-movement/fund-movement';
import { PettyCashRoles } from '../../../models/roles/roles-group';
import { ExpenseSettlementProxyService } from '../../../proxies/expense-settlement.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { Currency, currencyLabel, currencySymbol } from '../../../utilities/currency.util';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';
import { ControlsOf } from '../../../utilities/form.types';
import { BankChargesOption } from '../../shared/bank-charges-section/bank-charges-section.component';
import { SettlementSummaryRow } from '../../shared/settlement-summary/settlement-summary.component';
import { cashDocumentStatusLabel, cashDocumentStatusSeverity } from '../cash-document-status.util';

const LIST_ROUTE = '/expense-settlements';
const NEW_ID = 'new';

type LineGroup = FormGroup<ControlsOf<ExpenseSettlementLineFormModel>>;

/**
 * One expense settlement (Liquidación, #5799), laid out like an AP payment: the fund or trip instead of the provider,
 * its posted claims with an outstanding amount instead of AP documents, and — for a trip — its posted advances, which
 * net against the claims so the bank side says whether cash leaves or the employee refunds. The applied amount is
 * fixed to each document's outstanding for now (the API takes partial amounts). The treasurer saves and posts it.
 */
@Component({
  selector: 'app-expense-settlement-details',
  templateUrl: './expense-settlement-details.component.html',
})
export class ExpenseSettlementDetailsComponent implements OnInit {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly proxy = inject(ExpenseSettlementProxyService);
  private readonly userAuth = inject(UserAuthenticationService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly canSettle = this.userAuth.roles.includes(PettyCashRoles.PettyCashTreasurer);
  readonly fieldLabels = expenseSettlementFieldLabels;
  readonly ownerCodes = ReceiptOwnerCode;
  readonly directions = ExpenseSettlementDirection;

  readonly isLoading = signal(0);
  readonly isNew = signal(true);
  readonly currentId = signal<string | null>(null);
  readonly settlement = signal<ExpenseSettlement | null>(null);
  readonly owners = signal<ExpenseSettlementOwner[]>([]);
  readonly banks = signal<FundingBankAccount[]>([]);
  readonly preview = signal<ExpenseSettlementJournalPreview | null>(null);

  /** New mode: the fund or trip, as "code:reference". */
  readonly ownerControl = new FormControl<string | null>(null, Validators.required);

  readonly form = new FormGroup<ControlsOf<ExpenseSettlementFormModel>>({
    settlementDate: new FormControl<Date | null>(new Date(), Validators.required),
    bankAccount: new FormControl<string | null>(null),
    bankingReference: new FormControl<string | null>(null),
    bankingFee: new FormControl<number | null>(0, Validators.min(0)),
    securityFee: new FormControl<number | null>(0, Validators.min(0)),
    note: new FormControl<string | null>(null),
  });
  readonly claimRows = new FormArray<LineGroup>([]);
  readonly advanceRows = new FormArray<LineGroup>([]);

  /** Mirrors the form and rows for the computed summary, including loads made with emitEvent: false. */
  private readonly formValue = signal(this.form.getRawValue());
  private readonly claimValues = signal<ExpenseSettlementLineFormModel[]>([]);
  private readonly advanceValues = signal<ExpenseSettlementLineFormModel[]>([]);

  readonly saveMenuItems: MenuItem[] = [
    { label: this.label('Save and Close', 'Save and Close'), icon: 'pi pi-save', command: () => this.save(true) },
  ];

  readonly status = computed(() => this.settlement()?.status ?? ExpenseSettlementStatus.Draft);
  readonly isPosted = computed(() => this.status() === ExpenseSettlementStatus.Posted);
  readonly isEditable = computed(() => this.canSettle && !this.isNew() && !this.isPosted());
  readonly isTrip = computed(() => this.settlement()?.owner.entityDefinitionCode === ReceiptOwnerCode.Trip);
  readonly currency = computed<Currency | null>(() => this.settlement()?.currency ?? null);

  readonly headerTitle = computed(() =>
    this.isNew() ? this.label('ExpenseSettlement_New_Title', 'New expense settlement') : (this.settlement()?.number ?? ''),
  );

  readonly ownerOptions = computed(() =>
    this.owners().map((o) => ({
      label: `${o.name} · ${o.owner.number} — ${o.claims} ${this.label('ExpenseSettlement_Owner_Claims', 'claims')} · ${currencySymbol(o.currency)} ${o.outstanding.toFixed(2)}`,
      value: `${o.owner.entityDefinitionCode}:${o.owner.reference}`,
    })),
  );

  readonly claimsTotal = computed(() => this.sumSelected(this.claimValues()));
  readonly advancesTotal = computed(() => this.sumSelected(this.advanceValues()));
  readonly net = computed(() => this.round(this.claimsTotal() - this.advancesTotal()));
  readonly direction = computed(() =>
    this.net() > 0
      ? ExpenseSettlementDirection.Outgoing
      : this.net() < 0
        ? ExpenseSettlementDirection.Incoming
        : ExpenseSettlementDirection.None,
  );
  readonly selectedClaims = computed(() => this.claimValues().filter((r) => r.selected).length);
  readonly selectedAdvances = computed(() => this.advanceValues().filter((r) => r.selected).length);
  readonly charges = computed(() => {
    const value = this.formValue();
    return this.round((value.bankingFee ?? 0) + (value.securityFee ?? 0));
  });

  readonly bankOptions = computed<BankChargesOption[]>(() =>
    this.banks().map((bank) => ({
      label: [bank.code, bank.bankName, bank.name, `****${bank.bankAccountNumber.slice(-4)}`].filter((s) => !!s).join(' · '),
      value: bank.reference,
    })),
  );

  readonly summaryRows = computed<SettlementSummaryRow[]>(() =>
    this.isTrip()
      ? [{ label: this.label('ExpenseSettlement_Summary_Advances', 'Advances netted'), value: -this.advancesTotal(), tone: 'muted' }]
      : [],
  );
  readonly summaryNetLabel = computed(() => {
    switch (this.direction()) {
      case ExpenseSettlementDirection.Outgoing:
        return this.isTrip()
          ? this.label('ExpenseSettlement_Net_PayEmployee', 'Paid to the employee')
          : this.label('ExpenseSettlement_Net_Replenish', 'Replenishment leaving the bank');
      case ExpenseSettlementDirection.Incoming:
        return this.label('ExpenseSettlement_Net_Refund', 'Refund from the employee');
      default:
        return this.label('ExpenseSettlement_Net_None', 'No cash moves');
    }
  });
  /** What the bank actually moves: out = net + charges; in = refund − charges. */
  readonly bankAmount = computed(() => {
    const net = Math.abs(this.net());
    return this.direction() === ExpenseSettlementDirection.Outgoing
      ? this.round(net + this.charges())
      : this.direction() === ExpenseSettlementDirection.Incoming
        ? this.round(net - this.charges())
        : 0;
  });

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.formValue.set(this.form.getRawValue()));
    this.claimRows.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.claimValues.set(this.claimRows.getRawValue()));
    this.advanceRows.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.advanceValues.set(this.advanceRows.getRawValue()));
  }

  ngOnInit(): void {
    const query = this.route.snapshot.queryParamMap;
    const id = query.get('id');
    if (_.isNil(id)) {
      this.back();
      return;
    }
    this.isNew.set(id === NEW_ID);
    if (this.isNew()) {
      this.breadcrumbService.setCurrent('Common_New');
      const ownerReference = query.get('ownerReference');
      const ownerCode = query.get('ownerCode');
      if (!_.isNil(ownerReference) && !_.isNil(ownerCode)) this.ownerControl.setValue(`${ownerCode}:${ownerReference}`);
      this.loadOwners();
    } else {
      this.currentId.set(id);
      this.loadItem(id);
    }
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private loadOwners(): void {
    this.startLoading();
    this.proxy
      .getOwners()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => this.owners.set(response.data ?? []));
  }

  private loadItem(id: string): void {
    this.startLoading();
    this.proxy
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const settlement = response.data;
        if (_.isNil(settlement)) return;
        this.breadcrumbService.setCurrent(settlement.number);
        if (settlement.status === ExpenseSettlementStatus.Draft) {
          this.loadSettleable(settlement);
          this.loadBanks(settlement.id);
        } else {
          this.setSettlement(settlement, null);
          this.loadBanks(settlement.id);
        }
      });
  }

  /** A draft also lists the owner's other outstanding claims and advances, unticked. */
  private loadSettleable(settlement: ExpenseSettlement): void {
    this.startLoading();
    this.proxy
      .getSettleable(settlement.owner.reference, settlement.owner.entityDefinitionCode)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => this.setSettlement(settlement, response.data ?? null));
  }

  private loadBanks(id: string): void {
    this.proxy.getBankAccounts(id).subscribe((response) => this.banks.set(response.data ?? []));
  }

  private loadPreview(id: string): void {
    this.proxy.getJournalPreview(id).subscribe((response) => this.preview.set(response.data ?? null));
  }

  private setSettlement(settlement: ExpenseSettlement, settleable: ExpenseSettleable | null): void {
    this.settlement.set(settlement);
    this.form.patchValue(
      {
        settlementDate: this.fromApiDate(settlement.settlementDate),
        bankAccount: settlement.bankAccountReference,
        bankingReference: settlement.bankingReference,
        bankingFee: settlement.transferFee,
        securityFee: settlement.securityTax,
        note: settlement.note,
      },
      { emitEvent: false },
    );
    this.fillRows(this.claimRows, settlement.claims, settleable?.claims ?? []);
    this.fillRows(this.advanceRows, settlement.advances, settleable?.advances ?? []);
    if (this.isEditable()) {
      this.form.enable({ emitEvent: false });
      this.claimRows.controls.forEach((row) => row.controls.selected.enable({ emitEvent: false }));
      this.advanceRows.controls.forEach((row) => row.controls.selected.enable({ emitEvent: false }));
    } else {
      this.form.disable({ emitEvent: false });
      this.claimRows.disable({ emitEvent: false });
      this.advanceRows.disable({ emitEvent: false });
    }
    this.form.markAsPristine();
    this.formValue.set(this.form.getRawValue());
    this.claimValues.set(this.claimRows.getRawValue());
    this.advanceValues.set(this.advanceRows.getRawValue());
    if (settlement.status === ExpenseSettlementStatus.Draft) this.loadPreview(settlement.id);
  }

  /** The settlement's own lines ticked, then the owner's other outstanding documents unticked. */
  private fillRows(
    rows: FormArray<LineGroup>,
    applied: (ExpenseSettlementClaim | ExpenseSettlementAdvance)[],
    open: (ExpenseSettlementClaim | ExpenseSettlementAdvance)[],
  ): void {
    rows.clear({ emitEvent: false });
    const appliedRefs = new Set(applied.map((a) => a.reference));
    for (const line of applied) rows.push(this.row(line, true), { emitEvent: false });
    for (const line of open.filter((o) => !appliedRefs.has(o.reference))) rows.push(this.row(line, false), { emitEvent: false });
  }

  private row(line: ExpenseSettlementClaim | ExpenseSettlementAdvance, selected: boolean): LineGroup {
    const total = 'total' in line ? line.total : line.amount;
    return new FormGroup<ControlsOf<ExpenseSettlementLineFormModel>>({
      reference: new FormControl(line.reference, { nonNullable: true }),
      number: new FormControl(line.number, { nonNullable: true }),
      total: new FormControl(total, { nonNullable: true }),
      outstanding: new FormControl(line.outstanding, { nonNullable: true }),
      selected: new FormControl(selected, { nonNullable: true }),
      // Fixed to the outstanding for now; the API already takes a partial amount.
      appliedAmount: new FormControl({ value: line.outstanding, disabled: true }, { nonNullable: true }),
    });
  }

  /** New mode: opens the Draft for the chosen fund or trip with every outstanding claim (and advance) ticked. */
  create(): void {
    if (this.ownerControl.invalid || _.isNil(this.ownerControl.value)) {
      this.ownerControl.markAsTouched();
      GlobalMessageUtil.warn(this.title(), this.label('ExpenseSettlement_Owner_Required', 'Choose the fund or trip to settle.'));
      return;
    }
    const [code, reference] = this.ownerControl.value.split(':');
    this.startLoading();
    this.proxy
      .create({ owner: { reference, entityDefinitionCode: Number(code) } })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const created = response.data;
        if (_.isNil(created)) return;
        this.isNew.set(false);
        this.currentId.set(created.id);
        this.router.navigate([], { queryParams: { id: created.id }, replaceUrl: true });
        this.breadcrumbService.setCurrent(created.number);
        this.loadSettleable(created);
        this.loadBanks(created.id);
      });
  }

  save(shouldClose = false): void {
    if (this.isNew()) {
      this.create();
      return;
    }
    if (!this.validate(false)) return;
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.startLoading();
    this.proxy
      .update(id, this.payload())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (_.isNil(response.data)) return;
        GlobalMessageUtil.success(this.title(), this.label('ExpenseSettlement_Saved', 'Settlement saved.'));
        if (shouldClose) {
          this.back();
          return;
        }
        this.loadSettleable(response.data);
      });
  }

  /** Saves first so the posted figures are exactly what is on screen, then posts. */
  confirmPost(): void {
    if (!this.validate(true)) return;
    const message = this.label(
      'ExpenseSettlement_Post_Message',
      'Post this settlement? It books the entry, records the bank movement and settles the selected claims. It cannot be undone here.',
    );
    this.confirmationService.confirm({
      header: this.label('ExpenseSettlement_Post_Title', 'Post settlement'),
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('ExpenseSettlement_Post', 'Post'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => this.post(),
    });
  }

  private post(): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.startLoading();
    this.proxy
      .update(id, this.payload())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((saved) => {
        if (_.isNil(saved.data)) return;
        this.startLoading();
        this.proxy
          .post(id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe((response) => {
            if (_.isNil(response.data)) return;
            GlobalMessageUtil.success(this.title(), this.label('ExpenseSettlement_Posted', 'Settlement posted.'));
            this.preview.set(null);
            this.setSettlement(response.data, null);
          });
      });
  }

  confirmDelete(): void {
    const settlement = this.settlement();
    if (_.isNil(settlement)) return;
    this.confirmationService.confirm({
      header: this.label('ExpenseSettlement_Delete_Title', 'Delete draft'),
      message: this.label('ExpenseSettlement_Delete_Message', 'Delete this draft settlement and its attachments?'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('Delete', 'Delete'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.startLoading();
        this.proxy
          .delete(settlement.id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe(() => {
            GlobalMessageUtil.success(this.title(), this.label('ExpenseSettlement_Deleted', 'Draft deleted.'));
            this.back();
          });
      },
    });
  }

  onFile(file: File): void {
    const id = this.currentId();
    if (_.isNil(id)) return;
    this.startLoading();
    this.proxy
      .addAttachment(id, file)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const updated = response.data;
        if (!_.isNil(updated)) this.settlement.update((current) => (current ? { ...current, attachments: updated.attachments } : updated));
      });
  }

  openAttachment(url: string | null | undefined): void {
    if (url) window.open(url, '_blank');
  }

  toggleAll(rows: FormArray<LineGroup>, selected: boolean): void {
    rows.controls.forEach((row) => row.controls.selected.setValue(selected));
  }

  openOwner(): void {
    const owner = this.settlement()?.owner;
    if (_.isNil(owner)) return;
    const route = owner.entityDefinitionCode === ReceiptOwnerCode.Trip ? '/trip-details' : '/petty-cash-fund-details';
    this.router.navigate([route], { queryParams: { id: owner.reference } });
  }

  openClaim(reference: string): void {
    this.router.navigate(['/expense-claim-details'], { queryParams: { id: reference } });
  }

  openAdvance(reference: string): void {
    this.router.navigate(['/employee-advance-details'], { queryParams: { id: reference } });
  }

  back(): void {
    this.router.navigate([LIST_ROUTE]);
  }

  statusLabel(status: ExpenseSettlementStatus): string {
    return cashDocumentStatusLabel(status, (k, f) => this.label(k, f));
  }

  statusSeverity(status: ExpenseSettlementStatus): 'success' | 'warn' | 'secondary' {
    return cashDocumentStatusSeverity(status);
  }

  symbol(): string {
    return currencySymbol(this.currency());
  }

  currencyCode(): string {
    return currencyLabel(this.currency());
  }

  getRow(row: unknown): LineGroup {
    return row as LineGroup;
  }

  getJournalRow(row: unknown): ExpenseClaimJournalLine {
    return row as ExpenseClaimJournalLine;
  }

  /** The bank is required to post only when cash moves; at least one claim always. */
  private validate(forPost: boolean): boolean {
    const bank = this.form.controls.bankAccount;
    bank.setValidators(forPost && this.direction() !== ExpenseSettlementDirection.None ? Validators.required : null);
    bank.updateValueAndValidity({ emitEvent: false });
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    if (this.selectedClaims() === 0) {
      GlobalMessageUtil.warn(this.title(), this.label('ExpenseSettlement_NoClaims', 'Tick at least one claim to settle.'));
      return false;
    }
    return true;
  }

  private payload(): ExpenseSettlementUpdate {
    const value = this.form.getRawValue();
    const applied = (rows: ExpenseSettlementLineFormModel[]) =>
      rows.filter((r) => r.selected).map((r) => ({ reference: r.reference, appliedAmount: r.appliedAmount }));
    return {
      claims: applied(this.claimRows.getRawValue()),
      advances: applied(this.advanceRows.getRawValue()),
      settlementDate: this.toApiDate(value.settlementDate ?? new Date()),
      bankAccountReference: value.bankAccount,
      bankingReference: value.bankingReference?.trim() || null,
      transferFee: value.bankingFee ?? 0,
      securityTax: value.securityFee ?? 0,
      note: value.note?.trim() || null,
    };
  }

  private sumSelected(rows: ExpenseSettlementLineFormModel[]): number {
    return this.round(rows.filter((r) => r.selected).reduce((sum, r) => sum + r.appliedAmount, 0));
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }

  /** yyyy-MM-dd of the picked local day, so the server stores that calendar day. */
  private toApiDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /** The stored calendar day as a local Date (the server keeps midnight UTC). */
  private fromApiDate(value: string | null): Date | null {
    if (_.isNil(value)) return null;
    const [year, month, day] = value.substring(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private title(): string {
    return this.label('ExpenseSettlement_Title', 'Expense settlements');
  }

  private label(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
